import { FC, ReactNode, useState } from 'react'

import { ProductImage } from './ProductImage'
import { TitleAndPrice } from './TitleAndPrice'
import { useWebLayout } from 'featureSliced/shared/hooks/useWebLayout'
import { UnitSellCodePrice } from './UnitSellCodePrice'
import { ProductInfoButton } from './ProductInfoButton'
import { ProductInfoInline } from './ProductInfoInline'
import { KitchenInfo } from './KitchenInfo'
import { Allergens } from './Allergens'
import { Description } from './Description'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { KitchenInfoItemType } from '../types'

import {
  ProductMainContainer,
  ProductMainContainerInner,
  LeadDaysWarning,
} from './styled'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { RenderIf } from 'components/RenderIf'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { handleLocalStorage } from 'featureSliced/shared/utils/handleLocalStorage'
import {
  formatLeadDaysModalWarning,
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'

type CPCProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
  currentAmount?: number
  /** the template's fixed components, rendered in the details column — passed
   * only by the shells that add them; the UPDATE modal lists components itself */
  includedSlot?: ReactNode
  kitchenInfoState: [
    KitchenInfoItemType,
    (value: (value: KitchenInfoItemType) => any) => void
  ]
}

export const CommonProductComponent: FC<CPCProps> = ({
  product,
  productTemplateId,
  currentAmount = 1,
  includedSlot = null,
  kitchenInfoState,
}) => {
  const t = useTranslations()
  const { isWeb } = useWebLayout()
  const leadDays = getItemLeadDays(product.ClickCollectLeadDaysCount)
  const isLeadDaysExceeded = isItemLeadDaysExceeded({
    leadDays,
    pickUpTime: handleLocalStorage('pickUpTime'),
  })

  return (
    <>
      <ProductMainContainer>
        <ProductImage product={product} productTemplateId={productTemplateId} />
        <ProductMainContainerInner>
          <TitleAndPrice
            product={product}
            productTemplateId={productTemplateId}
            currentAmount={currentAmount}
            infoSlot={isWeb ? null : <ProductInfoButton product={product} />}
          />
          <UnitSellCodePrice product={product} currentAmount={currentAmount} />
          <RenderIf condition={isWeb}>
            <ProductInfoInline product={product} />
          </RenderIf>
          <RenderIf condition={isLeadDaysExceeded}>
            <LeadDaysWarning>
              {formatLeadDaysModalWarning(t, leadDays)}
            </LeadDaysWarning>
          </RenderIf>
          {includedSlot}
          <KitchenInfo
            kitchenInfo={product.kitchenInfo}
            kitchenInfoState={kitchenInfoState}
          />
          <Allergens product={product} />
          <Description note={product.note ?? undefined} />
        </ProductMainContainerInner>
      </ProductMainContainer>
    </>
  )
}
